import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Radio, Search } from "lucide-react";
import { useChannels } from "@/api/queries/channels";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ListTableShell } from "@/components/ResourceShell";
import { relativeTime } from "@/lib/relativeTime";
import { cn } from "@/lib/utils";
import type { Channel } from "@/types";
import { CreateChannelDialog } from "@/components/channel-actions/CreateChannelDialog";
import { ProvisioningInstructionsDialog } from "@/components/ProvisioningInstructionsDialog";
import { OneTimeTokenDialog } from "@/components/OneTimeTokenDialog";
import { channelProvisioning, type CreatedChannelInfo } from "@/components/channel-actions/channelProvisioning";

const ROW_LIMIT = 50;

// #design-pass W5: Channel-Status → Badge-Variant. offline = unerwartet weg (rot),
// disabled = bewusst aus (grau), enrolled = pairing offen (blau), online = healthy (grün).
function statusVariant(s: Channel["status"]) {
  if (s === "online") return "online" as const;
  if (s === "enrolled") return "enrolled" as const;
  if (s === "disabled") return "disabled" as const;
  return "destructive" as const;
}

export function ChannelsList() {
  const { data, isLoading, isError, error, refetch } = useChannels();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<CreatedChannelInfo | null>(null);
  const [search, setSearch] = useState("");

  const q = search.trim().toLowerCase();
  const filtered = (data ?? []).filter((c) => !q || `${c.name} ${c.type} ${c.id}`.toLowerCase().includes(q));
  const rows = filtered.slice(0, ROW_LIMIT);
  const onlineCount = (data ?? []).filter((c) => c.status === "online").length;

  // #185/#186: Wenn der Master ohne NATS läuft (Dev), kommt kein Bundle —
  // dann nur den Bootstrap-Token im einfachen Dialog zeigen.
  const provisioning = created ? channelProvisioning(created) : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <p className="text-[13px] text-text3">
          {data?.length ?? 0} Channels · {onlineCount} online
        </p>
        <Button onClick={() => setCreateOpen(true)} data-testid="action-create-channel">
          <Plus className="h-4 w-4" />
          Neuer Channel
        </Button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] max-w-[360px] flex-1">
          <Search className="pointer-events-none absolute left-[11px] top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-text3" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Channel suchen (Name / Type / ID)…"
            data-testid="list-search"
            className="h-9 w-full rounded-lg border border-border bg-surface pl-[34px] pr-3 text-[13px] text-text outline-none placeholder:text-text3 focus:border-accent-border"
          />
        </div>
      </div>

      <ListTableShell
        isLoading={isLoading && !data}
        isError={isError}
        errorMessage={(error as Error | undefined)?.message}
        isEmpty={!isLoading && filtered.length === 0}
        emptyMessage={data && data.length > 0 ? "Kein Channel passt zur Suche" : "Noch keine Channels"}
        emptyHint={data && data.length > 0 ? "Suche anpassen" : "Klick 'Neuer Channel' zum Anlegen"}
        onRetry={() => refetch()}
        rowLimit={ROW_LIMIT}
        totalRows={filtered.length}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Channel</TableHead>
              <TableHead>Typ</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Externe Identität</TableHead>
              <TableHead className="text-right">Heartbeat</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((c) => {
              const ident = c.inventorySnapshot?.externalIdentity;
              return (
                <TableRow key={c.id} data-clickable="true" onClick={() => navigate(`/channels/${c.id}`)}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-border bg-surface2 text-text3">
                        <Radio className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <div className="font-semibold text-text">{c.name}</div>
                        <div className="mono text-[11.5px] text-text3">{c.id.slice(0, 12)}…</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="default">{c.type}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(c.status)}>
                      {c.status === "online" && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
                      {c.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {ident ? <span className="mono text-[12.5px] text-text2">{ident}</span> : <span className="text-text3">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={cn("text-[12.5px]", c.status === "online" ? "text-text2" : "text-text3")}>
                      {relativeTime(c.lastHeartbeatAt)}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ListTableShell>

      {createOpen && <CreateChannelDialog open onOpenChange={setCreateOpen} onCreated={setCreated} />}
      {/* #186: NATS-Bundle vorhanden → volle Provisioning-Anweisung. */}
      {created && provisioning && (
        <ProvisioningInstructionsDialog
          open
          onOpenChange={(o) => !o && setCreated(null)}
          title={`Channel erstellt: ${created.channelName}`}
          description="Plugin-Container mit diesem Bundle starten — dann pairt sich der Channel (z.B. WhatsApp-QR)."
          blocks={provisioning.blocks}
          runCommand={provisioning.runCommand}
          hints={provisioning.hints}
        />
      )}
      {/* Dev ohne NATS: kein Bundle → nur Bootstrap-Token. */}
      {created && !provisioning && (
        <OneTimeTokenDialog
          open
          onOpenChange={(o) => !o && setCreated(null)}
          title={`Channel erstellt: ${created.channelName}`}
          description="Master ohne NATS — nur Bootstrap-Token verfügbar."
          label="Channel-Token"
          token={created.channelToken}
        />
      )}
    </div>
  );
}
